import { Component, Input, OnInit } from '@angular/core';
import { Subscription, of, Observable, forkJoin, BehaviorSubject, from } from "rxjs";
import { tap } from "rxjs/operators";
import { GridOptions, ColDef, ColGroupDef, RedrawRowsParams, GridApi } from "ag-grid-community";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { WellService } from "../../services/well.service";
import { GridFormatterService } from '../../services/grid-formatter.service';
import { WellProductionReporting, ProductionReportingSearchSpec, Production } from "../../models/well.model";

@Component({
  selector: 'production-reporting',
  standalone: false,
  templateUrl: './production-reporting.component.html',
  styleUrl: './production-reporting.component.scss',
})
export class ProductionReportingComponent implements OnInit {

    @Input() apiNumber: string = "";
    @Input() organizationId: string = "";
    @Input() rbdmsOwnerNumber: string = "";


    @Input() public productionReports: WellProductionReporting[] = [];
    reportingGridApi?: GridApi;
    reportingGridOptions?: GridOptions;

    statusBar = {
		statusPanels: [
			{
				statusPanel: 'agFilteredRowCountComponent',
				align: 'right',
			},
			{
				statusPanel: 'agTotalRowCountComponent',
				align: 'right',
			}
		]
	};

    subscriptions: Subscription = new Subscription();


    constructor(private readonly dialogSvc: DynamicDialogService, private readonly _wellService: WellService, private readonly _formatter: GridFormatterService)
    {

    }

    ngOnInit() {
        this.reportingGridOptions = <GridOptions>{
			columnDefs: this.buildReportingColumns(),
			defaultColDef: { filter: true, sortable: true } as ColDef,
			enableBrowserTooltips: false,
			tooltipTrigger: 'focus',
			headerHeight: 50,
			groupHeaderHeight: 25,
			onFilterChanged: () => {
				var params = <RedrawRowsParams>{};
				if (this.reportingGridApi != null) {
                    this.reportingGridApi.redrawRows(params);
                }
			},
			onGridReady: (params) => {
				this.reportingGridApi = params.api;
			},
			pagination: false,
			overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Please wait ...</span>'
		}
        this.getProductionReports();
    }

    buildReportingColumns(): ColDef[] {
        let columns: ColDef[] = [            
            {
                headerName: "Production Reported By",
                children: [
                    {
                        headerClass: "wrapped",
                        headerName: "RICS Owner Number",
                        headerTooltip: "RICS Owner Number",
						field: "ricsOwnerNumber",
                        tooltipField: "ricsOwnerNumber",
                        filter: "agTextColumnFilter",
						filterParams: this._formatter.defaultTextFilterParams,
						maxWidth: 150,
						resizable: true
					},
					{
						headerClass: "wrapped",
						headerName: "RBDMS Owner Number",
						headerTooltip: "RBDMS Owner Number",
						field: "rbdmsOwnerNumber",
						tooltipField: "rbdmsOwnerNumber",
						filter: "agTextColumnFilter",
						filterParams: this._formatter.defaultTextFilterParams,
						maxWidth: 145,
						resizable: true
					},
                    {
                        headerClass: "wrapped",
                        headerName: "Owner Name",
                        headerTooltip: "Owner Name",
                        field: "ownerName",
                        tooltipField: "ownerName",
                        filter: "agTextColumnFilter",
						filterParams: this._formatter.defaultTextFilterParams,
						resizable: true
                    }
                ]
            } as ColGroupDef,
            {
                headerName: "Production Reported",
				children: [
					{
						headerClass: "wrapped",
						headerName: "API #",
						headerTooltip: "API #",
						field: "apiNumber",
						tooltipField: "apiNumber",
						filter: "agTextColumnFilter",
						filterParams: this._formatter.defaultTextFilterParams,
						maxWidth: 150,
						resizable: true
					},
                    {
                        headerClass: "wrapped",
                        headerName: "Year",
                        headerTooltip: "Year",
                        field: "reportingYear",
                        tooltipField: "reportingYear",
						filter: "agNumberColumnFilter",
						filterParams: this._formatter.defaultNumberFilterParams,
						maxWidth: 95,
						resizable: true
                    },
                    {
                        headerClass: "wrapped",
                        headerName: "Quarter",
                        headerTooltip: "Quarter",
                        field: "quarter",
						tooltipField: "quarter",
                        filter: "agNumberColumnFilter",
                        filterParams: this._formatter.defaultNumberFilterParams,
                        minWidth: 100,
						maxWidth: 125,
						resizable: true
                    },
                    {
                        headerClass: "wrapped",
                        headerName: "Oil (Bbls) Produced",
                        headerTooltip: "Oil (Bbls) Produced",
                        field: "oilProduced",
                        filter: "agNumberColumnFilter",
                        filterParams: this._formatter.defaultNumberFilterParams,
						tooltipField: "oilProduced",
						maxWidth: 115,
						resizable: true
                    },
                    {
                        headerClass: "wrapped",
                        headerName: "Gas (Mcf) Produced",
                        headerTooltip: "Gas (Mcf) Produced",
                        field: "gasProduced",
                        filter: "agNumberColumnFilter",
                        filterParams: this._formatter.defaultNumberFilterParams,
						tooltipField: "gasProduced",
						maxWidth: 115,
						resizable: true
                    },
                    {
                        headerClass: "wrapped",
                        headerName: "Brine (Bbls) Produced",
                        headerTooltip: "Brine (Bbls) Produced",
                        field: "brineProduced",
                        filter: "agNumberColumnFilter",
                        filterParams: this._formatter.defaultNumberFilterParams,
						tooltipField: "brineProduced",
						maxWidth: 115,
						resizable: true
                    },
                    {
                        headerClass: "wrapped",
                        headerName: "Days in Production",
                        headerTooltip: "Days in Production",
                        field: "daysInProduction",
                        filter: "agNumberColumnFilter",
                        filterParams: this._formatter.defaultNumberFilterParams,
						tooltipField: "daysInProduction",
                        minWidth: 120,
						maxWidth: 135,
						resizable: true
					},
					{
						headerClass: "wrapped",
						headerName: "Date Added",
						headerTooltip: "Date Added",
						field: "dateAdded",
						filter: "agDateColumnFilter",
						filterParams: this._formatter.defaultDateFilterParams,
						valueFormatter: this._formatter.formatDate("dateAdded", "MM/dd/yyyy", "N/A", "+0000"),
						maxWidth: 115,
						resizable: true
					},
					{
						headerClass: "wrapped",
						headerName: "Production Comments",
						headerTooltip: "Production Comments",
						field: "productionComments",
						tooltipField: "productionComments",
						minWidth: 100,
						resizable: true
					}
                ]
            } as ColGroupDef
        ];

        this._formatter.applyFilterKeyboardHintTooltips(columns);
        return columns;
    }


    getProductionReports() {
        console.log('production report request made from subcomponent');
        if (this.reportingGridApi) {
            this.reportingGridApi.setGridOption("loading", true);
        }

         let spec: ProductionReportingSearchSpec = {
            apiNumber: this.apiNumber,
            ricsOwnerNumber: this.organizationId,
            rbdmsOwnerNumber: this.rbdmsOwnerNumber
        } as ProductionReportingSearchSpec;
        let obs: Observable<WellProductionReporting[]> = this._wellService.searchProductionReporting(spec);
        let subs$: Subscription = obs.pipe(
            tap((data: WellProductionReporting[]) => {
                this.productionReports = data;
                if (this.reportingGridApi != null) {
                    this.reportingGridApi.setGridOption("rowData", this.productionReports);
                    this.reportingGridApi.setGridOption("loading", false);
                }
            })
        ).subscribe({
            next:  () => { },
            error: (err: any) => {
                console.error(err);
                if (this.reportingGridApi) this.reportingGridApi.setGridOption("loading", false);
                this.dialogSvc.showError();
            }
        }
           
        );
        this.subscriptions.add(subs$);
    };
  
}
